import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../../../config/firebase";

/**
 * Custom hook for managing version history state and operations
 * Uses localStorage caching to reduce Firestore reads
 */
export const useVersionHistory = (isVersionHistoryExpanded) => {
  const [versionHistories, setVersionHistories] = useState({});
  const [loadingVersions, setLoadingVersions] = useState(false);
  const CACHE_KEY = "portfolio_version_history";

  // Convert Firestore Timestamp to ISO string for storage
  const convertTimestampForStorage = (version) => {
    const converted = { ...version };
    if (
      converted.timestamp &&
      typeof converted.timestamp.toDate === "function"
    ) {
      // Firestore Timestamp - convert to ISO string
      converted.timestamp = converted.timestamp.toDate().toISOString();
    } else if (converted.timestamp instanceof Date) {
      // Already a Date - convert to ISO string
      converted.timestamp = converted.timestamp.toISOString();
    } else if (typeof converted.timestamp === "string") {
      // Already a string - keep it
    }
    return converted;
  };

  // Save version history to localStorage
  const saveToLocalStorage = (histories) => {
    try {
      // Convert timestamps to ISO strings for JSON serialization
      const historiesForStorage = {};
      Object.keys(histories).forEach((sectionId) => {
        historiesForStorage[sectionId] = histories[sectionId].map(
          convertTimestampForStorage,
        );
      });
      localStorage.setItem(CACHE_KEY, JSON.stringify(historiesForStorage));
    } catch (error) {
      console.error("Error saving version history to localStorage:", error);
    }
  };

  // Load version history from localStorage
  const loadFromLocalStorage = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("Error loading version history from localStorage:", error);
      return null;
    }
  };

  // Fetch version history function - memoized to prevent infinite loops
  const fetchVersionHistory = useCallback(
    async (fromFirebase = true) => {
      try {
        setLoadingVersions(true);

        // If forcing Firebase fetch, clear the cache first
        if (fromFirebase) {
          localStorage.removeItem(CACHE_KEY);
        } else {
          // If not forcing Firebase fetch, try localStorage first
          const cached = loadFromLocalStorage();
          if (cached) {
            setVersionHistories(cached);
            setLoadingVersions(false);
            return;
          }
        }

        // Fetch from Firestore
        const versionsRef = collection(db, "versions");
        const q = query(versionsRef, orderBy("timestamp", "desc"), limit(5));
        const snapshot = await getDocs(q);

        const allVersions = snapshot.docs.map((doc) => {
          const data = doc.data();
          // Convert Firestore Timestamp to ISO string immediately
          if (data.timestamp && typeof data.timestamp.toDate === "function") {
            return {
              ...data,
              id: doc.id,
              timestamp: data.timestamp.toDate().toISOString(),
            };
          }
          return {
            ...data,
            id: doc.id,
          };
        });

        const histories = {
          about: [],
          contact: [],
          qualification: [],
          home: [],
          skills: [],
          certificates: [],
          projects: [],
          defaultThemes: [],
        };

        allVersions.forEach((version) => {
          if (histories[version.sectionId]) {
            histories[version.sectionId].push(version);
          }
        });

        setVersionHistories(histories);
        // Save to localStorage for next time
        saveToLocalStorage(histories);
      } catch (error) {
        console.error("Error fetching version history:", error);
      } finally {
        setLoadingVersions(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // saveToLocalStorage is a local function that doesn't need to be in dependencies
  );

  // Lazy load version history when accordion is expanded
  useEffect(() => {
    if (!isVersionHistoryExpanded) return;
    // Load from localStorage first (no Firebase read)
    fetchVersionHistory(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVersionHistoryExpanded]); // fetchVersionHistory is memoized with useCallback

  const getVersionHistory = (sectionId) => {
    return versionHistories[sectionId] || [];
  };

  const isCurrentPublishedVersion = (sectionId, versionData) => {
    const history = getVersionHistory(sectionId);
    if (history.length === 0) return false;
    const lastPublished = history[0];
    try {
      return JSON.stringify(versionData) === JSON.stringify(lastPublished.data);
    } catch (error) {
      return false;
    }
  };

  return {
    versionHistories,
    loadingVersions,
    getVersionHistory,
    isCurrentPublishedVersion,
    refetchVersionHistory: fetchVersionHistory,
  };
};
